import { ReactNode } from "react";
import useIsPhone from "features/files/use-is-phone";

type CatalogTableProps = {
  /**
   * `key` picks the cell out of each row's `cells`; `className` is applied to the header and its cells
   * of the table. On a phone the first column is the title of the row's card and `className` is not used.
   */
  columns: { key: string; header: string; className?: string }[];
  rows: { id: string; cells: Record<string, ReactNode> }[];
  /** Adds a last, unnamed column with this per row, e.g. a ChangeLink. */
  renderRowAction?: (id: string) => ReactNode;
  /** Shown instead of the table when there are no rows. */
  empty: ReactNode;
  /** True until the first load ends: with no rows yet, nothing is shown rather than `empty`. */
  isLoading?: boolean;
  /** Replaces `empty` when the load failed, so a failed load does not look like an empty list. */
  loadError?: ReactNode;
};

function hasContent(cell: ReactNode): boolean {
  return cell !== null && cell !== undefined && cell !== false && cell !== "";
}

export function CatalogTable({
  columns,
  rows,
  renderRowAction,
  empty,
  isLoading,
  loadError,
}: CatalogTableProps) {
  const isPhone = useIsPhone();

  if (rows.length === 0) {
    if (isLoading) return null;
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
        {loadError ?? empty}
      </div>
    );
  }

  // A phone has no room for the columns side by side, so each row becomes a card:
  // the first column as its title, the other filled columns as "header: value" lines,
  // then the row's action. The card is `relative` so that a link in its title can stretch
  // over the whole card (see the name link of the users list).
  if (isPhone) {
    const [titleColumn, ...detailColumns] = columns;
    return (
      <ul className="divide-y divide-gray-100 border-y border-gray-100">
        {rows.map((row) => {
          const action = renderRowAction?.(row.id);
          return (
            <li key={row.id} className="relative py-3">
              <div className="break-words text-sm text-gray-900">
                {row.cells[titleColumn.key]}
              </div>
              <dl className="mt-1 space-y-1 text-sm">
                {detailColumns
                  .filter((column) => hasContent(row.cells[column.key]))
                  .map((column) => (
                    <div key={column.key} className="flex gap-2">
                      <dt className="shrink-0 text-gray-500">{column.header}:</dt>
                      <dd className="min-w-0 break-words text-gray-900">
                        {row.cells[column.key]}
                      </dd>
                    </div>
                  ))}
              </dl>
              {action && <div className="mt-3">{action}</div>}
            </li>
          );
        })}
      </ul>
    );
  }

  // On a touch screen the rows are 44px high, so the 44px tap areas of the row actions (ChangeLink) of two rows do not overlap.
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs font-medium text-gray-500">
            {columns.map((column) => (
              <th
                key={column.key}
                className={`py-2 pr-4 font-medium ${column.className ?? ""}`}
              >
                {column.header}
              </th>
            ))}
            {renderRowAction && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-gray-100 last:border-b-0"
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`py-2.5 [@media(pointer:coarse)]:py-3 pr-4 align-top text-gray-900 ${
                    column.className ?? ""
                  }`}
                >
                  {row.cells[column.key]}
                </td>
              ))}
              {renderRowAction && (
                <td className="py-2.5 [@media(pointer:coarse)]:py-3 pr-2.5 text-right align-top">
                  {renderRowAction(row.id)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
